import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Loader2, MapPin, Calendar, Hash, Trash2 } from 'lucide-react';
import { listProjects, createProject, deleteProject } from '../api/client';

const STATUS_STYLES = {
  uploaded: 'bg-slate-500/10 text-slate-300 border-slate-500/30',
  processing: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  review_required: 'bg-red-500/10 text-red-300 border-red-500/30',
  ready: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  final: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', postcode: '', job_number: '', inspection_date: '' });
  const navigate = useNavigate();

  const fetchProjects = async (q) => {
    setLoading(true);
    try {
      const data = await listProjects(q);
      setProjects(data);
    } catch (e) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  useEffect(() => {
    const handle = setTimeout(() => fetchProjects(search || undefined), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Project name is required');
    setCreating(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v && String(v).trim())
      );
      const project = await createProject(payload);
      toast.success('Project created');
      setDialogOpen(false);
      setForm({ name: '', address: '', postcode: '', job_number: '', inspection_date: '' });
      navigate(`/portal/evidence/projects/${project.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (project, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${project.name}"? This will hide all its files. This cannot be undone.`)) return;
    try {
      await deleteProject(project.id);
      toast.success('Project deleted');
      fetchProjects(search || undefined);
    } catch {
      toast.error('Failed to delete project');
    }
  };

  return (
    <div className="space-y-6" data-testid="evidence-dashboard">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Assessment evidence projects</h1>
          <p className="text-sm text-slate-400 mt-1">
            Post-assessment evidence packs. Assessment result files define outcomes; genuine
            evidence supports them.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-project-btn"
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Evidence Pack
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg">
            <DialogHeader>
              <DialogTitle>New Evidence Pack</DialogTitle>
              <DialogDescription className="text-slate-400">
                Start a new project. You can drag files in after this.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitCreate} className="space-y-3" data-testid="new-project-form">
              <div>
                <Label htmlFor="np-name" className="text-slate-300">Project name *</Label>
                <Input
                  id="np-name" data-testid="np-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="bg-slate-800 border-slate-700"
                  placeholder="e.g. Unit 7 Marsh Barton Trading Estate"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="np-jobnum" className="text-slate-300">Job number</Label>
                  <Input
                    id="np-jobnum" data-testid="np-jobnum"
                    value={form.job_number}
                    onChange={(e) => setForm({ ...form, job_number: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                    placeholder="EPD-1234"
                  />
                </div>
                <div>
                  <Label htmlFor="np-date" className="text-slate-300">Inspection date</Label>
                  <Input
                    id="np-date" data-testid="np-date" type="date"
                    value={form.inspection_date}
                    onChange={(e) => setForm({ ...form, inspection_date: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="np-addr" className="text-slate-300">Address</Label>
                <Input
                  id="np-addr" data-testid="np-address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <Label htmlFor="np-pc" className="text-slate-300">Postcode</Label>
                <Input
                  id="np-pc" data-testid="np-postcode"
                  value={form.postcode}
                  onChange={(e) => setForm({ ...form, postcode: e.target.value.toUpperCase() })}
                  className="bg-slate-800 border-slate-700 uppercase"
                  placeholder="EX1 1AA"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button
                  type="submit"
                  disabled={creating}
                  data-testid="np-submit"
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="project-search"
          placeholder="Search by name, address, postcode or job number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-slate-900 border-slate-800 text-slate-100"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
        </div>
      ) : projects.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800 border-dashed">
          <CardContent className="py-16 text-center text-slate-400" data-testid="empty-state">
            <p className="text-lg font-medium mb-1">No evidence packs yet</p>
            <p className="text-sm">Create your first project to start ingesting an assessment.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((p) => (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/portal/evidence/projects/${p.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/portal/evidence/projects/${p.id}`);
                }
              }}
              data-testid={`project-card-${p.id}`}
              className="text-left group focus:outline-none focus:ring-2 focus:ring-emerald-400/50 rounded-xl cursor-pointer"
            >
              <Card className="bg-slate-900 border-slate-800 group-hover:border-emerald-500/50 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-lg leading-tight pr-2">{p.name}</h3>
                    <span className={`text-[10px] font-medium uppercase tracking-wider border rounded-full px-2 py-0.5 ${STATUS_STYLES[p.status] || STATUS_STYLES.uploaded}`}>
                      {p.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="space-y-1.5 text-xs text-slate-400 mt-3">
                    {p.address && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>{p.address}{p.postcode ? `, ${p.postcode}` : ''}</span>
                      </div>
                    )}
                    {p.job_number && (
                      <div className="flex items-center gap-2">
                        <Hash className="w-3 h-3" /><span>{p.job_number}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      <span>Inspected {formatDate(p.inspection_date)} · Created {formatDate(p.created_at)}</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between">
                    <span className="text-sm text-slate-300">
                      <strong className="text-emerald-400">{p.total_files}</strong> file{p.total_files === 1 ? '' : 's'}
                    </span>
                    <button
                      type="button"
                      aria-label="Delete project"
                      data-testid={`delete-project-${p.id}`}
                      onClick={(e) => handleDelete(p, e)}
                      className="text-slate-500 hover:text-red-400 p-1 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
